import React, {
  createContext,
  useContext,
  useState,
  useMemo,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import localforage from 'localforage';
import moment from 'moment';

export const Context = createContext({});

const customersStore = localforage.createInstance({
  name: 'Customers',
});

const visitsStore = localforage.createInstance({
  name: 'TodayVisits',
});

const VISIT_SOURCES = {
  VISIT: 'visit',
  ORDER: 'order',
};

const getLegacyTodayVisitsCount = async () => {
  const customers = await customersStore.getItem('nodes');
  if (!Array.isArray(customers)) return 0;

  return customers.reduce(
    (sum, customer) => sum + (Number(customer?.dailyVisitsCount) || 0),
    0,
  );
};

const normalizeCustomerVisitSources = (sources) => ({
  visit: Math.max(0, Number(sources?.visit) || 0),
  order: Math.max(0, Number(sources?.order) || 0),
});

const countTrackedVisitCustomers = (customersById = {}) =>
  Object.values(customersById).reduce((count, sources) => {
    const normalized = normalizeCustomerVisitSources(sources);
    return normalized.visit > 0 || normalized.order > 0 ? count + 1 : count;
  }, 0);

export const getStoredTodayVisitsState = async (dateKey) => {
  const data = await visitsStore.getItem(dateKey);

  if (Number.isFinite(data)) {
    return { count: data, customersById: {} };
  }

  if (data && typeof data === 'object' && !Array.isArray(data)) {
    const customersById =
      data.customersById && typeof data.customersById === 'object'
        ? data.customersById
        : {};
    const count = Number.isFinite(data.count)
      ? data.count
      : countTrackedVisitCustomers(customersById);

    return { count, customersById };
  }

  // Legacy values stored selected customer IDs, not real visits.
  if (Array.isArray(data)) {
    return { count: await getLegacyTodayVisitsCount(), customersById: {} };
  }

  return { count: 0, customersById: {} };
};

export const Provider = (props) => {
  const { children } = props;
  const visitsLoadedRef = useRef(false);

  useEffect(() => {
    const pruneOldVisitKeys = async () => {
      try {
        const cutoff = moment().subtract(7, 'days').format('YYYY-MM-DD');
        const keys = await visitsStore.keys();
        await Promise.all(
          keys.filter((key) => key < cutoff).map((key) => visitsStore.removeItem(key)),
        );
      } catch {
        // non-critical
      }
    };
    pruneOldVisitKeys();
  }, []);
  const [todayVisitsDate, setTodayVisitsDate] = useState(() =>
    moment().format('YYYY-MM-DD'),
  );

  const refreshTodayDate = useCallback(() => {
    const current = moment().format('YYYY-MM-DD');
    setTodayVisitsDate((prev) => (prev !== current ? current : prev));
  }, []);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [loadingComments, setLoadingComments] = useState(false);
  const [refetchCustomers, setRefetchCustomers] = useState(false);
  const [refetchProducts, setRefetchProducts] = useState(false);
  const [refetchComments, setRefetchComments] = useState(false);
  const [loadingTasks, setLoadingTasks] = useState(false);
  const [refetchTasks, setRefetchTasks] = useState(false);
  const [taskModificationsVersion, setTaskModificationsVersion] = useState(0);
  const incrementTaskModifications = useCallback(() => setTaskModificationsVersion((v) => v + 1), []);

  const [loadingReports, setLoadingReports] = useState(false);
  const [refetchReports, setRefetchReports] = useState(false);

  const [loadingOpenItems, setLoadingOpenItems] = useState(false);
  const [refetchOpenItems, setRefetchOpenItems] = useState(false);

  const [refetchOrders, setRefetchOrders] = useState(false);
  const [surveyCompletionVersion, setSurveyCompletionVersion] = useState(0);

  const [todayVisitsState, setTodayVisitsState] = useState({
    count: 0,
    customersById: {},
  });

  useEffect(() => {
    let isCancelled = false;

    visitsLoadedRef.current = false;
    setTodayVisitsState({ count: 0, customersById: {} });

    const loadTodayVisitsCount = async () => {
      try {
        const storedState = await getStoredTodayVisitsState(todayVisitsDate);
        if (!isCancelled) setTodayVisitsState(storedState);
      } catch (err) {
        console.error('Error loading today visits count:', err);
        if (!isCancelled) {
          setTodayVisitsState({ count: 0, customersById: {} });
        }
      } finally {
        if (!isCancelled) visitsLoadedRef.current = true;
      }
    };

    loadTodayVisitsCount();

    return () => {
      isCancelled = true;
    };
  }, [todayVisitsDate]);

  useEffect(() => {
    const msUntilNextDay = moment()
      .endOf('day')
      .add(1, 'millisecond')
      .diff(moment());

    const timeoutId = setTimeout(() => {
      setTodayVisitsDate(moment().format('YYYY-MM-DD'));
    }, Math.max(msUntilNextDay, 1));

    return () => clearTimeout(timeoutId);
  }, [todayVisitsDate]);

  const registerTodayVisitCustomer = useCallback(
    (customerId, source = VISIT_SOURCES.VISIT) => {
      if (!customerId || !Object.values(VISIT_SOURCES).includes(source)) return;

      const normalizedCustomerId = String(customerId);

      setTodayVisitsState((prev) => {
        const existingSources = normalizeCustomerVisitSources(
          prev.customersById?.[normalizedCustomerId],
        );

        const alreadyTracked =
          existingSources.visit > 0 || existingSources.order > 0;

        return {
          count: alreadyTracked ? prev.count : prev.count + 1,
          customersById: {
            ...prev.customersById,
            [normalizedCustomerId]: {
              ...existingSources,
              [source]: existingSources[source] + 1,
            },
          },
        };
      });
    },
    [],
  );

  const unregisterTodayVisitCustomer = useCallback(
    (customerId, source = VISIT_SOURCES.ORDER) => {
      if (!customerId || !Object.values(VISIT_SOURCES).includes(source)) return;

      const normalizedCustomerId = String(customerId);

      setTodayVisitsState((prev) => {
        const existingSources = normalizeCustomerVisitSources(
          prev.customersById?.[normalizedCustomerId],
        );

        if (existingSources[source] <= 0) {
          return prev;
        }

        const nextSources = {
          ...existingSources,
          [source]: Math.max(0, existingSources[source] - 1),
        };
        const shouldKeepCustomer =
          nextSources.visit > 0 || nextSources.order > 0;
        const nextCustomersById = { ...prev.customersById };

        if (shouldKeepCustomer) {
          nextCustomersById[normalizedCustomerId] = nextSources;
        } else {
          delete nextCustomersById[normalizedCustomerId];
        }

        return {
          count:
            shouldKeepCustomer || prev.count <= 0 ? prev.count : prev.count - 1,
          customersById: nextCustomersById,
        };
      });
    },
    [],
  );

  const incrementSurveyCompletionVersion = useCallback(() => {
    setSurveyCompletionVersion((prev) => prev + 1);
  }, []);

  useEffect(() => {
    if (!visitsLoadedRef.current) return;
    if (todayVisitsState.count <= 0) {
      visitsStore.removeItem(todayVisitsDate).catch((err) => {
        console.error('Error clearing today visits count:', err);
      });
      return;
    }

    visitsStore
      .setItem(todayVisitsDate, {
        count: todayVisitsState.count,
        customersById: todayVisitsState.customersById,
      })
      .catch((err) => {
        console.error('Error saving today visits count:', err);
      });
  }, [todayVisitsDate, todayVisitsState]);

  const todayVisitsCount = todayVisitsState.count;
  const todayVisitedCustomers = todayVisitsState.customersById;

  const localforageContext = useMemo(
    () => ({
      customersStore,
      loadingCustomers,
      setLoadingCustomers,
      loadingProducts,
      setLoadingProducts,
      refetchCustomers,
      setRefetchCustomers,
      refetchProducts,
      setRefetchProducts,
      loadingTasks,
      setLoadingTasks,
      refetchTasks,
      setRefetchTasks,
      taskModificationsVersion,
      incrementTaskModifications,
      loadingComments,
      setLoadingComments,
      setRefetchComments,
      refetchComments,
      loadingReports,
      setLoadingReports,
      refetchReports,
      setRefetchReports,
      loadingOpenItems,
      setLoadingOpenItems,
      refetchOpenItems,
      setRefetchOpenItems,
      refetchOrders,
      setRefetchOrders,
      surveyCompletionVersion,
      incrementSurveyCompletionVersion,
      todayDate: todayVisitsDate,
      refreshTodayDate,
      todayVisitsCount,
      todayVisitedCustomers,
      registerTodayVisitCustomer,
      unregisterTodayVisitCustomer,
    }),
    [
      loadingCustomers,
      loadingProducts,
      refetchCustomers,
      refetchProducts,
      loadingTasks,
      refetchTasks,
      taskModificationsVersion,
      incrementTaskModifications,
      loadingComments,
      refetchComments,
      loadingReports,
      refetchReports,
      loadingOpenItems,
      refetchOpenItems,
      refetchOrders,
      surveyCompletionVersion,
      incrementSurveyCompletionVersion,
      todayVisitsDate,
      refreshTodayDate,
      todayVisitsCount,
      todayVisitedCustomers,
      registerTodayVisitCustomer,
      unregisterTodayVisitCustomer,
    ],
  );

  return (
    <Context.Provider value={localforageContext}>{children}</Context.Provider>
  );
};

export const { Consumer } = Context;

export const useLocalForageContext = () => useContext(Context);
